import React from 'react';

import {
  DtoBase,
  TenantEnums,
  TenantModels,
} from '@project44-models/iam-gateway';

import { ReactComponent } from '../../../../../shared/abstract/infrastructrue/ReactComponent';
import { ComponentPropsValueObject } from '../../../../../shared/core/domain/ComponentPropsValueObject';
import { TAnyOfPermissionsHOC } from '../../../../../shared/core/infrastructrue/types';
import { TypographyComponent } from '../../../../../shared/core/infrastructrue/ui/Typography/Typography.component';
import { TTypography } from '../../../../../shared/core/infrastructrue/ui/Typography/Typography.view';
import { TenantTileCardView, TTenantTileCard } from './TenantTileCard.view';

export class TenantTileCardComponent extends ReactComponent {
  public buildAvatarName(tenantName: string): string {
    const tenantNameCollection = tenantName
      .replace(/[^a-zA-Z]+/g, String(' '))
      .trim()
      .split(' ')
      .filter(Boolean);

    switch (tenantNameCollection.length) {
      case 0:
        return '-';
      case 1:
        return this.modelFirstLetterToUpperCase(tenantNameCollection[0]);
      default:
        const firstLetter = this.modelFirstLetterToUpperCase(
          tenantNameCollection[0],
        );
        const secondLetter = this.modelFirstLetterToUpperCase(
          tenantNameCollection[1],
        );

        return `${firstLetter}${secondLetter}`;
    }
  }

  public composeMainText(
    tenantDto: DtoBase<TenantModels.TenantViewModel>,
    highlightableConfig?: TTypography['highlightableConfig'],
    isLoading?: boolean,
  ): React.ReactElement<TTypography> {
    return this.composeTypographyComponent(
      {
        'aria-label': 'tenant-tile-card-TenantName',
        content: tenantDto.source.getName(),
        role: 'presentation',
        variant: 'subtext',
      },
      highlightableConfig?.searchQuery === tenantDto.source.getName()
        ? undefined
        : highlightableConfig,
      { isLoading },
    );
  }

  public composeTenantPurposeEnumText(
    tenantPurposeEnum: TenantEnums.PurposeEnum,
  ): string {
    const tenantPurposeEnumMap: Record<TenantEnums.PurposeEnum, string> = {
      [TenantEnums.PurposeEnum.DEMO]: 'Demo',
      [TenantEnums.PurposeEnum.PROOF_OF_CONCEPT]: 'Proof of Concept',
      [TenantEnums.PurposeEnum.INTERNAL_TEST]: 'Internal Test',
      [TenantEnums.PurposeEnum.TEST]: 'Test',
      [TenantEnums.PurposeEnum.PRODUCTION]: 'Production',
      [TenantEnums.PurposeEnum.ARCHIVED]: 'Archived',
      [TenantEnums.PurposeEnum.INTERNAL_INFRASTRUCTURE]:
        'Internal Infrastructure',
    };

    return tenantPurposeEnumMap[tenantPurposeEnum];
  }

  public composeSubtext(
    tenantDto: DtoBase<TenantModels.TenantViewModel>,
    highlightableConfig?: TTypography['highlightableConfig'],
    isCurrentTenant?: boolean,
    isLoading?: boolean,
  ): React.ReactElement<TTypography> {
    const componentProps: Omit<TTypography, 'config'> = {
      highlightableConfig: undefined,
      typographyProps: {
        'aria-label': 'tenant-tile-card-TenantSubtext',
        content: '',
        role: 'presentation',
        variant: 'caption',
      },
      withLoadingStatusOptions: { isLoading },
    };

    switch (true) {
      case isCurrentTenant:
        return this.composeTypographyComponent(
          {
            ...componentProps.typographyProps,
            content: 'Current Tenant',
          },
          undefined,
          undefined,
        );
      case !!highlightableConfig:
        const { searchQuery } = highlightableConfig as NonNullable<
          TTypography['highlightableConfig']
        >;

        const tenantSubtextContent: string = tenantDto.source.getTenantId();

        return this.composeTypographyComponent(
          {
            ...componentProps.typographyProps,
            content: tenantSubtextContent,
          },
          tenantSubtextContent === searchQuery
            ? undefined
            : highlightableConfig,
          { isLoading },
        );
      default:
        return this.composeTypographyComponent(
          {
            ...componentProps.typographyProps,
            content: tenantDto.source.getTenantUuid(),
          },
          undefined,
          { isLoading },
        );
    }
  }

  public static create(
    props: TAnyOfPermissionsHOC<Omit<TTenantTileCard, 'config'>>,
  ): React.ReactElement<TTenantTileCard> {
    return this.withAnyOfPermissionsHOC(
      ['system-admin:auth:switch-to-any-tenant'],
      TenantTileCardView,
      {
        ...props,
        config: new TenantTileCardComponent(),
      },
    );
  }

  private modelFirstLetterToUpperCase(payload: string): string {
    return payload.charAt(0).toUpperCase();
  }

  private composeTypographyComponent(
    typographyProps: TTypography['typographyProps'],
    highlightableConfig?: TTypography['highlightableConfig'],
    withLoadingStatusOptions?: TTypography['withLoadingStatusOptions'],
  ): React.ReactElement<TTypography> {
    return TypographyComponent.create(
      ComponentPropsValueObject.create({
        highlightableConfig,
        typographyProps,
        withLoadingStatusOptions,
      }),
    );
  }
}
